import React from 'react';
import PropTypes from 'prop-types';
import { ArrowRight } from 'lucide-react';
import Button from '../../Button';
import Hyperlink from '../../Hyperlink';
import './HeroBannerV2.css';

/**
 * HeroBannerV2 — full-photo hero with a glass subtitle tag, editorial title,
 * copy, a CTA button and a secondary action — a second BUTTON (`secondary*`, e.g.
 * black next to the white CTA: two equally weighted choices) or a text link (`link*`).
 *
 * Figma: Desktop `8775:198358` (1360×600 inside the layout gutters, text left),
 *        Mobile `8789:203434` (full-bleed, 740 tall, text centred on top).
 *
 * - `image` is the desktop photo; `imageMobile` falls back to it.
 * - Every text row renders only when it has content.
 * - Reuses DS Button (primary / white) and Hyperlink (large).
 */
const HeroBannerV2 = ({
  subtitle = '',
  title = '',
  copy = '',

  ctaLabel = '',
  ctaVariant = 'white',
  ctaIcon = <ArrowRight />,
  ctaOnClick,
  ctaHref,

  secondaryLabel = '',
  secondaryVariant = 'black',
  secondaryIcon = <ArrowRight />,
  secondaryOnClick,
  secondaryHref,

  linkLabel = '',
  linkHref,
  linkOnClick,

  image,
  imageMobile,
  imageAlt = '',
  className = '',
}) => {
  const ctaProps = {};
  if (ctaHref) {
    ctaProps.as = 'a';
    ctaProps.href = ctaHref;
  }
  if (ctaOnClick) ctaProps.onClick = ctaOnClick;

  const secondaryProps = {};
  if (secondaryHref) {
    secondaryProps.as = 'a';
    secondaryProps.href = secondaryHref;
  }
  if (secondaryOnClick) secondaryProps.onClick = secondaryOnClick;

  // Hyperlink defaults to href="#" — don't let a click-only link touch the URL.
  const handleLinkClick = (e) => {
    if (!linkHref) e.preventDefault();
    linkOnClick?.(e);
  };

  return (
    <section className={`hero-v2${className ? ` ${className}` : ''}`}>
      <div className="hero-v2__container">
        <div className="hero-v2__box">
          {image && (
            <picture className="hero-v2__media">
              <source media="(min-width: 1024px)" srcSet={image} />
              <img className="hero-v2__image" src={imageMobile || image} alt={imageAlt} />
            </picture>
          )}

          <div className="hero-v2__content">
            {subtitle && <p className="typo-h5 hero-v2__subtitle">{subtitle}</p>}
            {title && <h1 className="typo-h1-editorial hero-v2__title">{title}</h1>}
            {copy && <p className="typo-body-lg hero-v2__copy">{copy}</p>}

            {(ctaLabel || secondaryLabel || linkLabel) && (
              <div className="hero-v2__cta-row">
                {ctaLabel && (
                  <Button size="primary" variant={ctaVariant} iconRight={ctaIcon} {...ctaProps}>
                    {ctaLabel}
                  </Button>
                )}
                {secondaryLabel && (
                  <Button size="primary" variant={secondaryVariant} iconRight={secondaryIcon} {...secondaryProps}>
                    {secondaryLabel}
                  </Button>
                )}
                {linkLabel && (
                  <Hyperlink
                    variant="large"
                    href={linkHref || '#'}
                    onClick={handleLinkClick}
                    className="hero-v2__link"
                  >
                    {linkLabel}
                  </Hyperlink>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

HeroBannerV2.propTypes = {
  subtitle: PropTypes.string,
  title: PropTypes.string,
  copy: PropTypes.string,

  ctaLabel: PropTypes.string,
  ctaVariant: PropTypes.oneOf([
    'main', 'alternative', 'danger', 'outline', 'black', 'white', 'gray', 'text',
  ]),
  ctaIcon: PropTypes.node,
  ctaOnClick: PropTypes.func,
  ctaHref: PropTypes.string,

  secondaryLabel: PropTypes.string,
  secondaryVariant: PropTypes.oneOf([
    'main', 'alternative', 'danger', 'outline', 'black', 'white', 'gray', 'text',
  ]),
  secondaryIcon: PropTypes.node,
  secondaryOnClick: PropTypes.func,
  secondaryHref: PropTypes.string,

  linkLabel: PropTypes.string,
  linkHref: PropTypes.string,
  linkOnClick: PropTypes.func,

  image: PropTypes.string,
  imageMobile: PropTypes.string,
  imageAlt: PropTypes.string,
  className: PropTypes.string,
};

export default HeroBannerV2;
